import React, { Fragment, useEffect, useRef, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { filterOptions } from './config/filterOptions'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { usePagination } from '../../hooks/usePagination'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { VasRule } from '../../interfaces/VasRule/VasRule'
import { GenericOrderBy } from '../../components/_EOS/Table'
import { deleteVasRule, getVasRuleList } from './config/api'
import { GenericTableV2 } from '../../components/_EOS/GenericTable'
import { Status } from '../../hooks/useFetchState'
import { getColumnsConfig } from './config/tableColumns'
import { useModal } from '../../hooks/useModal'
import { CreateVasRuleModal } from './modal/CreateVasRuleModal'
import { Modal } from '../../components/_EOS/Modal'
import { DataCaptureCostumizationModal } from './modal/DataCaptureCostumizationModal'
import { Web, Delete, Edit, Visibility } from '@mui/icons-material'
import { EditVasRuleModal } from './modal/EditVasRuleModal'
import { ViewVasRuleModal } from './modal/ViewVasRuleModal'

interface VasRulePageProps {}

export const VasRulePage: React.FC<VasRulePageProps> = () => {
  const { callApi, state } = useApi<ApiResponse<VasRule>>()
  const { callApi: requestDeleteVasRule } = useApi()
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const pagination = usePagination(5)

  const [orderBy, setOrderBy] = useState<GenericOrderBy<VasRule>>({
    field: 'name' as keyof VasRule,
    direction: 'asc'
  })

  const columnsConfig = getColumnsConfig()

  const handleSorting = (field: keyof VasRule, direction: 'asc' | 'desc') => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const requestData = (filters?: Filter[], page?: number, pageSize?: number) => {
    callApi(getVasRuleList(filters, page, pageSize, orderBy)).catch((error) => {
      openSnackBar(error, false)
    })
  }

  // Refrescar solo cuando cambia el ordenamiento
  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)
    requestData(selectedFilters, 0, pagination.pageSize)
  }, [orderBy])

  const createModal = useModal('Crear Regla VAS')
  const createSubmitRef = useRef<(() => void) | null>(null)
  const createFormReadyRef = useRef<boolean>(false)
  const editModal = useModal<number>('Editar Regla VAS')
  const editSubmitRef = useRef<(() => void) | null>(null)
  const editFormReadyRef = useRef<boolean>(false)
  const viewModal = useModal<number>('Detalle de regla VAS')

  const DataCaptureModal = useModal<VasRule>('Personalización de captura de datos')
  const dataCaptureRef = useRef<(() => void) | null>(null)
  const deleteModal = useModal<VasRule>('Confirmar Eliminación')

  const handleCreateClick = () => {
    createModal.openModal('lg')
  }

  const handleUpdateSuccess = () => {
    requestData() // Refrescar sin cambiar parámetros
  }

  const handleDelete = (vasRuleId: number) => {
    requestDeleteVasRule(deleteVasRule(vasRuleId))
      .then(() => {
        openSnackBar('Regla VAS eliminada exitosamente', true)
        handleUpdateSuccess() // Recarga los datos
        deleteModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error.message || 'Error al eliminar', false)
      })
  }


  return (
    <Fragment>
      <StandardPage
        pageTitle=''
      >
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={(filters) => {
            pagination.setPagination(0, pagination.pageSize)
            requestData(filters, 0, pagination.pageSize)
          }}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeSearchOnClean={true}
          onAddButtonClick={handleCreateClick}
          createButtonTooltip='Crear Regla VAS'
        />
        <GenericTableV2<VasRule>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='vasRuleId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof VasRule,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'capture',
              label: 'Captura de Datos',
              onClick: (row) => DataCaptureModal.openModal('lg', row),
              icon: <Web />,
              shouldRender: (item) => item.vasRuleStateId === 3
            },
            {
              key: 'view',
              label: 'Detalle de regla VAS',
              onClick: (row) => viewModal.openModal('md', row.vasRuleId),
              icon: <Visibility />
            },
            {
              key: 'editVasRule',
              label: 'Editar',
              onClick: (item) => editModal.openModal('lg', item.vasRuleId),
              icon: <Edit />
            },
            {
              key: 'deleteVasRule',
              label: 'Eliminar',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />
            }
          ]}
        />
      </StandardPage>
      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
        cancelButton={{ title: 'Cerrar', action: createModal.closeModal }}
        successButton={{ title: 'Guardar', action: () => createSubmitRef.current?.(), disabled: !createFormReadyRef.current }}
      >
        <CreateVasRuleModal
          handleSnackbar={openSnackBar}
          handleCloseModal={createModal.closeModal}
          requestVasRule={() => { requestData() }}
          submitRef={createSubmitRef}
          isFormReadyRef={createFormReadyRef}
        />
      </Modal>
      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
        cancelButton={{ title: 'Cerrar', action: editModal.closeModal }}
        successButton={{ title: 'Guardar', action: () => editSubmitRef.current?.(), disabled: !editFormReadyRef.current }}
      >
        <EditVasRuleModal
          vasRuleId={editModal.extendedProps || 0}
          handleSnackbar={openSnackBar}
          handleCloseModal={editModal.closeModal}
          onUpdateSuccess={handleUpdateSuccess}
          submitRef={editSubmitRef}
          isFormReadyRef={editFormReadyRef}
        />
      </Modal>
      <Modal
        open={viewModal.isOpen}
        maxWidth={viewModal.size}
        title={viewModal.title}
        onClose={viewModal.closeModal}
        cancelButton={{ title: 'Cerrar', action: viewModal.closeModal }}
      >
        <ViewVasRuleModal vasRuleId={viewModal.extendedProps || 0} />
      </Modal>
      <Modal
        open={DataCaptureModal.isOpen}
        maxWidth={DataCaptureModal.size}
        title={DataCaptureModal.title}
        onClose={DataCaptureModal.closeModal}
        cancelButton={{ title: 'Cancelar', action: DataCaptureModal.closeModal }}
        successButton={{ title: 'Finalizar', action: () => dataCaptureRef.current?.() }}
      >
        <DataCaptureCostumizationModal
          vasRuleId={DataCaptureModal.extendedProps?.vasRuleId!}
          onClose={DataCaptureModal.closeModal}
          requestVasRule={() => { requestData() }}
          finalizeRef={dataCaptureRef}
        />
      </Modal>
      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        loading={state.status === Status.Loading}
        successButton={{
          action: () => handleDelete(deleteModal.extendedProps?.vasRuleId || 0),
          title: 'Confirmar'
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar'
        }}
      >
        <span>
          ¿Desea eliminar la Regla VAS:{' '}
          <strong>{deleteModal.extendedProps?.name}</strong>?
        </span>
      </Modal>
      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
